import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import { isNil } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Autocomplete from '~/components/core/Molecules/Fields/AutoComplete';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { getMoiAssignmentEmailUrl } from '~/components/exposures/moi/MoiUtils';
import DynamicTemplateFields from '~/components/GenericTemplates/FromTemplate/DynamicTemplateFields';
import type { TemplateMetadata, TemplateTypeKey } from '~/components/GenericTemplates/types';
import {
  FROM_TEMPLATE_FORM_KEYS,
  htmlToTextTemplateParser,
  TEMPLATES_TYPES,
} from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import { GENERIC_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { useCms } from '~/components/hooks/useCms';
import useOrganization from '~/components/OrganizationContext';
import GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES from '~/server_shared/generated-types/GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import {
  getAxiosParamsSerializer,
  getOrganizationCountryCode,
  isFeatureEnabled,
  reportAxiosError,
  reportErrorInProductionOrThrow,
} from '~/Utils';

const FETCH_PARAMS_SERIALIZER = getAxiosParamsSerializer('none');
const CLAIM_LEVEL_OPTION = 'claim_level';
const EXPOSURE_LEVEL_OPTION = 'exposure_level';

interface HandleCloseFromTemplateDialogParams {
  documentTemplateId?: number | null;
  documentTemplateExposureId?: number | null;
  requiresESignature?: boolean;
}

interface GenericTemplateSelectionContainerFormikProps {
  templateType: TemplateTypeKey;
  handleClose: (params?: HandleCloseFromTemplateDialogParams) => void;
  titleFieldId?: string;
  bodyFieldId: string;
  shouldConvertBodyHtmlToText?: boolean;
  signature?: string;
  setFailedTokens?: (tokens: string[]) => void;
  shouldOverrideTitle?: boolean;
  shouldContactInitialBody?: boolean;
  initialBody?: string;
  initialGenericTemplateId?: number;
  initialExposureId?: number;
  // When set, this is the MOI assignment-email modal: the picker also lists the org's MOI email
  // templates (via include_moi_email on the shared list), and picking one renders it against this MOI.
  moiId?: number;
  // The exposure the MOI email is being composed under. Forwarded so a re-render from the picker binds
  // the same exposure the dialog's own pre-fill did.
  moiExposureId?: number;
}

export interface DynamicFieldsFormValues {
  recipient_contact_id?: number | string;
  contact_id?: number | string;
  document_id?: number | string;
  payment_request_id?: number | string;
  e_signature_document_id?: number | string;
  e_signature_contact_id?: number | string;
}

interface GetFilledTemplateParams {
  templateId: number;
  recipientContactId?: number | string;
  contactId?: number | string;
  documentId?: number | string;
  paymentRequestId?: number | string;
  eSignatureDocumentId?: number | string;
  eSignatureContactId?: number | string;
}

export interface DynamicFieldsFormikProps {
  dynamicTokenFields: string[];
  selectedTemplateId?: number;
  handleClose: () => void;
  availableTemplates: TemplateMetadata[];
  selectedExposureId?: number;
  onSubmit: (values: DynamicFieldsFormValues) => Promise<void>;
  suppressESignature?: boolean;
}

export const DynamicFieldsFormik: React.FC<DynamicFieldsFormikProps> = ({
  dynamicTokenFields,
  selectedTemplateId,
  handleClose,
  availableTemplates,
  selectedExposureId,
  onSubmit,
  suppressESignature,
}) => {
  return (
    <Formik<DynamicFieldsFormValues>
      initialValues={{
        recipient_contact_id: '',
        contact_id: '',
        document_id: '',
        payment_request_id: '',
        e_signature_document_id: '',
        e_signature_contact_id: '',
      }}
      validationSchema={Yup.object().shape({
        recipient_contact_id: dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.RECIPIENT)
          ? Yup.number().nullable().required('Required')
          : Yup.number().nullable(),
        contact_id: dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.CONTACT)
          ? Yup.number().nullable().required('Required')
          : Yup.number().nullable(),
        document_id: dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.DOCUMENT)
          ? Yup.number().nullable().required('Required')
          : Yup.number().nullable(),
        payment_request_id: dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.PAYMENT)
          ? Yup.number().nullable().required('Required')
          : Yup.number().nullable(),
        e_signature_document_id:
          dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.E_SIGNATURE) && !suppressESignature
            ? Yup.number().nullable().required('Required')
            : Yup.number().nullable(),
        e_signature_contact_id:
          dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.E_SIGNATURE) && !suppressESignature
            ? Yup.number().nullable().required('Required')
            : Yup.number().nullable(),
      })}
      onSubmit={async (values) => await onSubmit(values)}
    >
      <DynamicFieldsInnerFormik
        dynamicTokenFields={dynamicTokenFields}
        selectedTemplateId={selectedTemplateId}
        handleClose={handleClose}
        availableTemplates={availableTemplates}
        exposureId={selectedExposureId}
        suppressESignature={suppressESignature}
      />
    </Formik>
  );
};

const GenericTemplateSelectionContainerFormik: React.FC<GenericTemplateSelectionContainerFormikProps> = ({
  templateType,
  handleClose,
  titleFieldId,
  bodyFieldId,
  shouldConvertBodyHtmlToText,
  signature,
  setFailedTokens,
  shouldOverrideTitle = true,
  shouldContactInitialBody = false,
  initialBody = '',
  initialGenericTemplateId,
  initialExposureId,
  moiId,
  moiExposureId,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationId } = useOrganization();
  const { userOrganization } = useCms();
  const { claim } = useClaim();
  const multiCountryOrganization = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );
  // The MOI assignment-email modal (moiId set) with the feature on: ask the shared list to also return
  // MOI email templates. They are exposure-level, so the is_claim_level filter scopes them to the
  // exposure-level view.
  const isMoiMode = !!moiId && isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.MOI_EMAIL_TEMPLATES);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  // A response is applied only if its request is still the latest one and the picker is still
  // mounted. The parent form outlives this dialog, so a late response would otherwise overwrite
  // whatever the user typed after the dialog closed.
  const latestRequestIdRef = useRef(0);
  useEffect(
    () => () => {
      latestRequestIdRef.current += 1;
    },
    []
  );

  const [availableTemplates, setAvailableTemplates] = useState<TemplateMetadata[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(initialGenericTemplateId);
  const [searchParams, setSearchParams] = useState({});
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplateLevel, setSelectedTemplateLevel] = useState<string | number>(
    initialGenericTemplateId
      ? initialExposureId && initialExposureId > 0
        ? EXPOSURE_LEVEL_OPTION
        : CLAIM_LEVEL_OPTION
      : ''
  );
  const [selectedExposureId, setSelectedExposureId] = useState<number>(initialExposureId ?? NaN);

  const isClaimLevel = selectedTemplateLevel === CLAIM_LEVEL_OPTION;
  const coverageType =
    !claim || isClaimLevel
      ? undefined
      : claim?.exposures?.find((exposure) => exposure.id === selectedExposureId)?.coverage_type;

  const getGenericTemplates = useCallback(
    async (claimLevel: boolean, coverageType?: string) => {
      if (!claim) {
        return;
      }
      try {
        const searchParams = {
          template_type: templateType,
          is_claim_level: claimLevel,
          lobs: [claim.lob],
          is_enabled: true,
          sub_organization_ids: claim.policy?.sub_organization?.id ? [claim.policy.sub_organization.id] : [],
          coverage_keys: claimLevel ? [] : [coverageType],
          states: claim.policy.policy_state ? [claim.policy.policy_state] : [],
          country: multiCountryOrganization
            ? claim.policy.policy_country || getOrganizationCountryCode(userOrganization)
            : undefined,
          // In the MOI modal, also pull the org's MOI email templates into this same list.
          include_moi_email: isMoiMode,
        };

        setIsLoadingTemplates(true);

        const { data } = await axios.get(GENERIC_TEMPLATES_ROUTES.GET_TEMPLATES(organizationId), {
          params: searchParams,
          paramsSerializer: FETCH_PARAMS_SERIALIZER,
        });
        setAvailableTemplates(data?.templates_metadata);
        setSearchParams(searchParams);
        setIsLoadingTemplates(false);
      } catch (error) {
        await reportAxiosError(error);
      }
    },
    [claim, organizationId, templateType, userOrganization, multiCountryOrganization, isMoiMode]
  );

  useEffect(() => {
    if (!selectedTemplateLevel || (selectedTemplateLevel === EXPOSURE_LEVEL_OPTION && !selectedExposureId)) {
      return;
    }

    getGenericTemplates(isClaimLevel, coverageType);
  }, [coverageType, isClaimLevel, selectedTemplateLevel, getGenericTemplates, selectedExposureId]);

  if (!claim) {
    reportErrorInProductionOrThrow('Tried to Render TemplateSelectionContainerFormik without claim it context');
    return (
      <CardDialog
        isDialog
        title={`Select ${TEMPLATES_TYPES[templateType]?.display_name} Template`}
        fullWidth
        width="sm"
        onClose={() => handleClose()}
      >
        <div>No claim in context, cant select template</div>
      </CardDialog>
    );
  }

  const attached_document_template_id = availableTemplates.filter(
    (template) => template.id === selectedTemplateId && !isNil(template.document_template_id)
  )[0]?.document_template_id;
  const shouldDisplayDocumentPreviewAlert = !isNil(attached_document_template_id);
  const hasAttachedDocumentTemplate = !isNil(attached_document_template_id);

  const dynamicTokenFields =
    availableTemplates.find((template) => template.id === selectedTemplateId)?.dynamic_token_fields || [];
  const getFilledTemplate = async ({
    templateId,
    recipientContactId,
    contactId,
    documentId,
    paymentRequestId,
    eSignatureDocumentId,
    eSignatureContactId,
  }: GetFilledTemplateParams) => {
    // A Select click reaches this bump without yielding to another task, so a Cancel cannot unmount
    // the picker first. Keep it ahead of any await: a call that started after unmount would never read
    // as stale.
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    const isStale = () => requestId !== latestRequestIdRef.current;
    try {
      const isMoiEmailTemplate =
        availableTemplates.find((template) => template.id === templateId)?.template_type === 'moi_email';

      if (isMoiEmailTemplate) {
        // MOI templates render server-side against this MOI (tokens need the claim/MOI ORM). The
        // endpoint returns the already-rendered {email_title, email_body} with the adjuster signature
        // appended, so — unlike the generic path — we don't append the signature here.
        // exposure_id is the exposure the email is composed under, deliberately not the picker's
        // selectedExposureId: the picker's exposure only filters which templates are offered, while the
        // render must stay bound to the exposure row the MOI card was opened from.
        const { data } = await axios.get(getMoiAssignmentEmailUrl(claim.id, moiId), {
          params: { template_id: templateId, exposure_id: moiExposureId },
        });
        if (isStale()) {
          return;
        }

        // Overwrite unconditionally: the MOI dialog reliably opens with failed tokens from its own
        // pre-fill render, so picking a template that resolved cleanly must clear that warning.
        if (setFailedTokens) {
          setFailedTokens(Array.isArray(data?.failed_token_display_names) ? data.failed_token_display_names : []);
        }

        setFieldValue(bodyFieldId, data.email_body);
        setFieldTouched(bodyFieldId, true);

        // A moi_email template's subject is optional (title_template is nullable and the server
        // renders it as ''), so guard on a non-empty title — the MOI dialog arrives with a real
        // pre-filled subject that must not be blanked.
        if (titleFieldId && shouldOverrideTitle && data.email_title) {
          setFieldValue(titleFieldId, htmlToTextTemplateParser(data.email_title, false));
          setFieldTouched(titleFieldId, true);
        }

        // This send is not attributed to a generic template. The dialog is re-openable, so clear any
        // id/context an earlier pick of a regular email template left behind — leaving them would
        // record the email against a template that contributed nothing to its content.
        setFieldValue(FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID, undefined);
        setFieldValue(FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT, undefined);

        // moi_email templates carry a document-template link like any other email template, and the
        // dialog's banner promises a preview when one is attached — forward the same payload the
        // generic path does so the promise holds. The render names the attachment of the template it
        // rendered, which is the one the server will generate.
        handleClose({
          documentTemplateId: data.document_template_id ?? null,
          documentTemplateExposureId: selectedExposureId,
          requiresESignature: dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.E_SIGNATURE),
        });
        return;
      }

      const url = GENERIC_TEMPLATES_ROUTES.GENERATE_FROM_TEMPLATE({
        organizationId,
        templateId,
        claimId: claim.id,
      });

      const { data } = await axios.post(url, {
        exposure_id: selectedExposureId,
        recipient_contact_id: recipientContactId,
        contact_id: contactId,
        document_id: documentId,
        payment_request_id: paymentRequestId,
        e_signature_document_id: eSignatureDocumentId,
        e_signature_contact_id: eSignatureContactId,
        is_preview: false,
      });
      if (isStale()) {
        return;
      }

      // Same overwrite-unconditionally rule as the MOI branch above: a clean template must clear the
      // warning a previously picked template (or the dialog's own pre-fill) left in the parent state.
      if (setFailedTokens) {
        setFailedTokens(Array.isArray(data?.failed_token_display_names) ? data.failed_token_display_names : []);
      }

      let bodyContent = shouldConvertBodyHtmlToText
        ? htmlToTextTemplateParser(data.body_template, true)
        : data.body_template;

      if (signature) {
        bodyContent += `\n${signature}`;
      }

      if (shouldContactInitialBody) {
        bodyContent += initialBody;
      }

      setFieldValue(bodyFieldId, bodyContent);
      setFieldTouched(bodyFieldId, true);

      setFieldValue(FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID, selectedTemplateId);
      const templateContext = {
        ...searchParams,
        recipient_contact_id: recipientContactId,
        contact_id: contactId,
        document_id: documentId,
        payment_request_id: paymentRequestId,
        e_signature_document_id: eSignatureDocumentId,
        e_signature_contact_id: eSignatureContactId,
      };
      setFieldValue(FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT, templateContext);

      if (titleFieldId && shouldOverrideTitle) {
        setFieldValue(titleFieldId, htmlToTextTemplateParser(data.title_template, false));
        setFieldTouched(titleFieldId, true);
      }

      if (data.additional_data) {
        Object.entries(data.additional_data).forEach(([key, value]) => {
          if (isNil(value)) {
            return;
          }
          setFieldValue(key, value);
        });
      }

      const requiresESignature = dynamicTokenFields.includes(GENERIC_TEMPLATES_DYNAMIC_TOKEN_TYPES.E_SIGNATURE);

      handleClose({
        documentTemplateId: attached_document_template_id,
        documentTemplateExposureId: selectedExposureId,
        requiresESignature,
      });
    } catch (error) {
      // A stale request's failure is still recorded, but the user already moved on from it, so it
      // must not open an error dialog over the composer or over the newer request's result.
      await reportAxiosError(error, !isStale());
    }
  };

  const shouldDisplayWarningBanner =
    !isLoadingTemplates &&
    ((selectedTemplateLevel === CLAIM_LEVEL_OPTION && availableTemplates.length === 0) ||
      (selectedTemplateLevel === EXPOSURE_LEVEL_OPTION && selectedExposureId && availableTemplates.length === 0));

  return (
    <CardDialog
      isDialog
      title={`Select ${TEMPLATES_TYPES[templateType]?.display_name} Template`}
      fullWidth
      width="sm"
      onClose={() => handleClose()}
    >
      {shouldDisplayWarningBanner ? (
        <AlertBanner
          note="No templates exist for the currently selected context"
          alertType={AlertBanner.ALERT_TYPES.WARNING}
        />
      ) : null}
      {shouldDisplayDocumentPreviewAlert ? (
        <AlertBanner
          note="Document template attached to email template. Will prompt preview."
          alertType={AlertBanner.ALERT_TYPES.INFO}
        />
      ) : null}
      <div className="my-12 w-full">
        <div className="my-12 w-full">
          <SingleSelectField
            label="Template Level"
            className="w-full"
            value={selectedTemplateLevel}
            onChange={(value: string | number) => {
              setSelectedTemplateLevel(value);
              setSelectedTemplateId(undefined);
            }}
            select
            disabled={isLoadingTemplates}
          >
            <MenuItem value={CLAIM_LEVEL_OPTION}>
              <ListItemText primary="Claim Level" />
            </MenuItem>

            <MenuItem value={EXPOSURE_LEVEL_OPTION}>
              <ListItemText primary="Exposure Level" />
            </MenuItem>
          </SingleSelectField>
        </div>

        <div className="my-12 w-full">
          <SingleSelectField
            label="Exposure"
            className="w-full"
            value={isNaN(selectedExposureId) ? '' : selectedExposureId}
            onChange={(value: unknown) => {
              setSelectedExposureId(value as number);
              setSelectedTemplateId(undefined);
            }}
            disabled={isLoadingTemplates || selectedTemplateLevel !== EXPOSURE_LEVEL_OPTION}
          >
            {claim.exposures.map((exposure) => (
              <MenuItem key={exposure.id} value={exposure.id}>
                <ListItemText primary={exposure.label_text} />
              </MenuItem>
            ))}
          </SingleSelectField>
        </div>

        <div className="my-12 w-full">
          <Autocomplete<{ template_name: string; id: number }>
            id="template_id"
            options={availableTemplates}
            getOptionLabel={(option: { template_name: string; id: number }) => option.template_name}
            disabled={availableTemplates.length === 0}
            onChange={(_, selectedTemplate) => setSelectedTemplateId(selectedTemplate?.id)}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            value={availableTemplates.find((template) => template.id === selectedTemplateId) || null}
            label="Template"
          />
        </div>
        <DynamicFieldsFormik
          dynamicTokenFields={dynamicTokenFields}
          selectedTemplateId={selectedTemplateId}
          handleClose={handleClose}
          availableTemplates={availableTemplates}
          selectedExposureId={selectedExposureId}
          suppressESignature={hasAttachedDocumentTemplate}
          onSubmit={async (values) => {
            if (!selectedTemplateId) {
              return reportErrorInProductionOrThrow(
                'getFilledTemplate should be disabled if there is no selectedTemplateId'
              );
            }
            await getFilledTemplate({
              templateId: selectedTemplateId,
              recipientContactId: values?.recipient_contact_id,
              contactId: values?.contact_id,
              documentId: values?.document_id,
              paymentRequestId: values?.payment_request_id,
              eSignatureDocumentId: values?.e_signature_document_id,
              eSignatureContactId: values?.e_signature_contact_id,
            });
          }}
        />
      </div>
    </CardDialog>
  );
};

interface DynamicFieldsFormProps {
  dynamicTokenFields: string[];
  selectedTemplateId?: number;
  handleClose: () => void;
  availableTemplates: TemplateMetadata[];
  exposureId?: number;
  suppressESignature?: boolean;
}

const DynamicFieldsInnerFormik: React.FC<DynamicFieldsFormProps> = ({
  dynamicTokenFields,
  selectedTemplateId,
  handleClose,
  availableTemplates,
  exposureId,
  suppressESignature,
}) => {
  const { submitForm, resetForm, isSubmitting } = useFormikContext();
  useEffect(() => {
    resetForm();
  }, [selectedTemplateId, resetForm]);

  return (
    <>
      {dynamicTokenFields ? (
        <DynamicTemplateFields
          dynamicTokenFields={dynamicTokenFields}
          exposureId={exposureId}
          suppressESignature={suppressESignature}
        />
      ) : null}
      <div className="mt-16 flex w-full justify-end">
        <CancelButton onClick={handleClose} />
        <Button
          variant="contained"
          color="primary"
          disabled={availableTemplates.length === 0 || !selectedTemplateId || isSubmitting}
          onClick={submitForm}
        >
          Select
        </Button>
      </div>
    </>
  );
};

export default GenericTemplateSelectionContainerFormik;
